<script setup lang="ts">
import { computed } from "vue";
import { UiCheckbox, UiIcon, UiTag, UiText, UiTooltip } from "@shopexperts/shared";

import PartnerDirectoryRecordAvatar from "./PartnerDirectoryRecordAvatar.vue";
import type { PartnerDirectoryRecord } from "@/app/services/partnerDirectoryApi.service";

const props = defineProps<{
  record: PartnerDirectoryRecord;
  active: boolean;
  checked: boolean;
}>();

const emit = defineEmits<{
  (e: "select"): void;
  (e: "toggle-checked", rangeSelect: boolean): void;
}>();

// UiCheckbox's own click handler emits the model update before a listener
// bound from here would run, so the modifier is read from the event that
// precedes it instead: mousedown for pointers, keydown for Space/Enter.
let rangeSelectHeld = false;

function captureRangeSelectModifier(event: MouseEvent | KeyboardEvent) {
  rangeSelectHeld = event.shiftKey;
}

const ogName = computed(() => props.record.name ?? props.record.sourceSlug ?? "Unknown expert");

// The og name stays visible whenever an admin-mapped name replaces it.
const mappedName = computed(() => {
  const name = props.record.mappedName?.trim();
  return name && name !== ogName.value ? name : null;
});

// Muted second line: og name when the display name is overridden, else the slug.
const secondaryLine = computed(() => {
  if (mappedName.value) return `Shopify: ${ogName.value}`;
  const slug = props.record.sourceSlug;
  return slug && slug !== ogName.value ? slug : null;
});

const expertTypeLabel = computed(() => {
  const expertType = (props.record.mappedPayload as { expert_type?: string } | null)?.expert_type ?? "agency";
  return expertType === "freelancer" ? "Freelancer" : "Agency";
});

const mappedKeyCount = computed(() => (props.record.mappedPayload ? Object.keys(props.record.mappedPayload).length : 0));

// Compact meta row: "★ 4.9 (123) · Toronto, Canada".
const metaLine = computed(() => {
  const parts: string[] = [];
  if (typeof props.record.rating === "number") {
    const reviews = typeof props.record.reviewCount === "number" ? ` (${props.record.reviewCount.toLocaleString()})` : "";
    parts.push(`★ ${props.record.rating.toFixed(1)}${reviews}`);
  }
  const location = props.record.primaryLocation ?? props.record.country;
  if (location) parts.push(location);
  return parts.join(" · ");
});

function statusVariant(status: string): "primary" | "secondary" | "rounded" | "dark" {
  if (status === "succeeded" || status === "approved" || status === "imported") return "primary";
  if (status === "failed" || status === "rejected") return "dark";
  if (status === "running" || status === "needs_review") return "rounded";
  return "secondary";
}

function formatStatus(status: string): string {
  return status.replace(/_/g, " ");
}
</script>

<template>
  <div
    class="flex w-full items-start gap-2 rounded-md px-2 py-2.5 text-left transition-colors hover:bg-se-gray-100"
    :class="{ 'bg-se-gray-100 ring-1 ring-inset ring-se-gray-300': props.active }"
  >
    <UiCheckbox
      class="mt-1 shrink-0 select-none"
      :model-value="props.checked"
      :name="`select-record-${props.record.id}`"
      @mousedown="captureRangeSelectModifier"
      @keydown="captureRangeSelectModifier"
      @click.stop
      @update:model-value="emit('toggle-checked', rangeSelectHeld)"
    />
    <button type="button" class="flex min-w-0 flex-1 items-start gap-2.5 text-left" @click="emit('select')">
      <PartnerDirectoryRecordAvatar :record="props.record" :alt="mappedName ?? ogName" class="h-9 w-9" />
      <div class="min-w-0 flex-1">
        <div class="flex items-start justify-between gap-2">
          <div class="min-w-0">
            <UiText as="span" variant="p-small" class="block truncate font-medium text-se-dark" :text="mappedName ?? ogName" />
            <UiText v-if="secondaryLine" as="span" variant="caption-normal" class="block truncate text-se-gray-500" :text="secondaryLine" />
          </div>
          <UiTooltip v-if="(props.record.noteCount ?? 0) > 0" content="Has screener notes">
            <div class="flex shrink-0 items-center gap-0.5 pt-0.5">
              <UiIcon name="chat-bubble" class="h-3.5 w-3.5 text-se-gray-500" />
              <UiText as="span" variant="caption-normal" class="text-se-gray-600" :text="String(props.record.noteCount)" />
            </div>
          </UiTooltip>
        </div>
        <UiText v-if="metaLine" as="span" variant="caption-normal" class="mt-0.5 block truncate text-se-gray-600" :text="metaLine" />
        <div class="mt-1.5 flex flex-wrap items-center gap-1.5">
          <UiTag :text="formatStatus(props.record.status)" :variant="statusVariant(props.record.status)" narrow />
          <UiText as="span" variant="caption-normal" class="text-se-gray-500" :text="expertTypeLabel" />
          <UiTag v-if="mappedKeyCount > 0" :text="`Mapped · ${mappedKeyCount}`" variant="secondary" narrow />
          <UiTag v-if="props.record.platformClaimed" text="Claimed" variant="secondary" narrow />
        </div>
      </div>
    </button>
  </div>
</template>
